import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONLABORATORY_LOAD,
    ACTION_INSTITUTIONLABORATORY_LOAD_BY_ID,
    ACTION_INSTITUTIONLABORATORY_LOAD_BY_LABORATORY_ID,
    ACTION_INSTITUTIONLABORATORY_LOAD_BY_INSTITUTION_ID,
    ACTION_INSTITUTIONLABORATORY_LOAD_FAILURE,
    ACTION_INSTITUTIONLABORATORY_LOAD_SUCCESS,

    ACTION_INSTITUTIONLABORATORY_SAVE,
    ACTION_INSTITUTIONLABORATORY_SAVE_SUCCESS,
    ACTION_INSTITUTIONLABORATORY_SAVE_FAILURE,

    ACTION_INSTITUTIONLABORATORY_CREATE,
    ACTION_INSTITUTIONLABORATORY_CREATE_SUCCESS,
    ACTION_INSTITUTIONLABORATORY_CREATE_FAILURE,

    ACTION_INSTITUTIONLABORATORY_DELETE,
    ACTION_INSTITUTIONLABORATORY_DELETE_SUCCESS,
    ACTION_INSTITUTIONLABORATORY_DELETE_FAILURE,

    ACTION_INSTITUTIONLABORATORY_VALIDATE,
    ACTION_INSTITUTIONLABORATORY_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONLABORATORY_VALIDATE_FAILURE,

    ACTION_INSTITUTIONLABORATORY_CLEAR,
    ACTION_INSTITUTIONLABORATORY_FILTER,

    InstitutionLaboratoryAction,
} from '../actions/institutionLaboratory';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionLaboratoryState {
    formData: Dtos.InstitutionLaboratory | Dtos.InstitutionLaboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    deleteState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionLaboratory[];
    formLength: number;
}

const initialState: IInstitutionLaboratoryState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    deleteState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionLaboratoryState, _isLoading: boolean): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IInstitutionLaboratoryState,
    formData: Dtos.InstitutionLaboratory | Dtos.InstitutionLaboratory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IInstitutionLaboratoryState, responseStatus: Dtos.ResponseStatus): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IInstitutionLaboratoryState, formFilter: IFormFilter): IInstitutionLaboratoryState {

    const items: Dtos.InstitutionLaboratory[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.institution?.name?.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IInstitutionLaboratoryState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IInstitutionLaboratoryState, propertiesToClear?: string[]): IInstitutionLaboratoryState {

    if (!propertiesToClear) {
        const newState: IInstitutionLaboratoryState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionLaboratoryState, _isSaving: boolean): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionLaboratoryState,
    formData: Dtos.InstitutionLaboratory | Dtos.InstitutionLaboratory[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionLaboratoryState, responseStatus: Dtos.ResponseStatus): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionLaboratoryState, _isCreating: boolean): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionLaboratoryState,
    formData: Dtos.InstitutionLaboratory,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionLaboratoryState, responseStatus: Dtos.ResponseStatus): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function deleting(state: IInstitutionLaboratoryState, _isDeleting: boolean): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function deleteSuccess(
    state: IInstitutionLaboratoryState,
): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function deleteFailure(state: IInstitutionLaboratoryState, responseStatus: Dtos.ResponseStatus): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        deleteState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IInstitutionLaboratoryState, _isValidating: boolean): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionLaboratoryState,
    formData: Dtos.InstitutionLaboratory,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionLaboratoryState, responseStatus: Dtos.ResponseStatus): IInstitutionLaboratoryState {
    const newState: IInstitutionLaboratoryState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const InstitutionLaboratoryReducer: Reducer<IInstitutionLaboratoryState> = (state: IInstitutionLaboratoryState = initialState, action: InstitutionLaboratoryAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONLABORATORY_LOAD:
        case ACTION_INSTITUTIONLABORATORY_LOAD_BY_ID:
        case ACTION_INSTITUTIONLABORATORY_LOAD_BY_LABORATORY_ID:
        case ACTION_INSTITUTIONLABORATORY_LOAD_BY_INSTITUTION_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONLABORATORY_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONLABORATORY_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONLABORATORY_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONLABORATORY_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONLABORATORY_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_DELETE:
            return deleting(state, true);
        case ACTION_INSTITUTIONLABORATORY_DELETE_SUCCESS:
            return deleteSuccess(state);
        case ACTION_INSTITUTIONLABORATORY_DELETE_FAILURE:
            return deleteFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONLABORATORY_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONLABORATORY_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default InstitutionLaboratoryReducer;